import { ReactNode } from "react";
import { Area, AreaChart, ResponsiveContainer } from "recharts";

import { Card, Text, Tooltip, View } from "../../base";
import { Icon } from "../../base/Icon";
import { cn } from "../../../utils/cn";
import { AgentDashboardPoint } from "./analyticsData";

export const ANALYTICS_COLORS = {
  primary: "#7C3AED",
  primaryLight: "#A78BFA",
  blue: "#2563EB",
  cyan: "#0891B2",
  green: "#059669",
  amber: "#D97706",
  red: "#DC2626",
  pink: "#DB2777",
};

type MetricCardProps = {
  label: string;
  value: string;
  detail: string;
  delta: number;
  isLowerBetter?: boolean;
  icon: string;
  color: string;
  data: AgentDashboardPoint[];
  dataKey: keyof AgentDashboardPoint;
};

export const MetricCard = ({ label, value, detail, delta, isLowerBetter = false, icon, color, data, dataKey }: MetricCardProps) => {
  const isImprovement = isLowerBetter ? delta <= 0 : delta >= 0;
  const isFlat = Math.abs(delta) < 0.05;

  return (
    <Card className="relative min-w-0 overflow-hidden border-gray-200 bg-white p-0 shadow-none transition-colors hover:border-gray-300 dark:border-gray-800 dark:bg-slate-900 dark:hover:border-gray-700">
      <View className="flex min-h-[126px] flex-col p-3.5">
        <View className="mb-2 flex items-center justify-between gap-2">
          <View className="flex min-w-0 items-center gap-1.5">
            <Icon
              name={icon}
              size={13}
              className="shrink-0 text-gray-400 dark:text-gray-500"
            />
            <Text className="truncate text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">{label}</Text>
          </View>
          <Tooltip content={detail}>
            <Icon
              name="info"
              size={12}
              className="shrink-0 text-gray-300 dark:text-gray-600"
            />
          </Tooltip>
        </View>

        <View className="flex items-end justify-between gap-2">
          <Text className="text-2xl font-semibold tabular-nums tracking-tight text-gray-950 dark:text-white">{value}</Text>
          <View
            className={cn(
              "mb-0.5 flex items-center gap-0.5 rounded px-1.5 py-0.5",
              isFlat
                ? "bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400"
                : isImprovement
                  ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400"
                  : "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400",
            )}
          >
            {!isFlat && (
              <Icon
                name={delta > 0 ? "arrow-up" : "arrow-down"}
                size={10}
              />
            )}
            <Text className="text-[10px] font-semibold tabular-nums">{isFlat ? "—" : `${Math.abs(delta).toFixed(1)}%`}</Text>
          </View>
        </View>

        <View className="mt-auto h-9 w-full pt-1">
          <ResponsiveContainer
            width="100%"
            height="100%"
          >
            <AreaChart
              data={data}
              margin={{ top: 3, right: 0, left: 0, bottom: 0 }}
            >
              <defs>
                <linearGradient
                  id={`spark-${dataKey}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={color}
                    stopOpacity={0.25}
                  />
                  <stop
                    offset="100%"
                    stopColor={color}
                    stopOpacity={0}
                  />
                </linearGradient>
              </defs>
              <Area
                type="monotone"
                dataKey={dataKey}
                stroke={color}
                strokeWidth={1.5}
                fill={`url(#spark-${dataKey})`}
                dot={false}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </View>
      </View>
    </Card>
  );
};

type DashboardPanelProps = {
  title: string;
  description: string;
  query?: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
};

export const DashboardPanel = ({ title, description, query, action, className, children }: DashboardPanelProps) => {
  return (
    <Card className={cn("min-w-0 overflow-hidden border-gray-200 bg-white p-0 shadow-none dark:border-gray-800 dark:bg-slate-900", className)}>
      <View className="flex min-h-[68px] items-start justify-between gap-3 border-b border-gray-100 px-4 py-3 dark:border-gray-800">
        <View className="min-w-0">
          <View className="flex items-center gap-1.5">
            <Text className="text-sm font-semibold text-gray-900 dark:text-white">{title}</Text>
            <Tooltip content={description}>
              <Icon
                name="info"
                size={12}
                className="text-gray-300 dark:text-gray-600"
              />
            </Tooltip>
          </View>
          {query && (
            <View className="mt-1 flex min-w-0 items-center gap-1.5">
              <View className="h-1.5 w-1.5 shrink-0 rounded-full bg-violet-500" />
              <Text className="truncate text-[10px] text-gray-400 dark:text-gray-500">{query}</Text>
            </View>
          )}
        </View>
        {action ?? (
          <button
            type="button"
            aria-label={`More options for ${title}`}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-300"
          >
            <Icon
              name="more-vertical"
              size={14}
            />
          </button>
        )}
      </View>
      {children}
    </Card>
  );
};

type LegendItemProps = {
  color: string;
  label: string;
  isDashed?: boolean;
};

export const LegendItem = ({ color, label, isDashed = false }: LegendItemProps) => (
  <View className="flex items-center gap-1.5">
    <View
      className={cn("h-0.5 w-4", isDashed && "border-t border-dashed bg-transparent")}
      style={isDashed ? { borderColor: color } : { backgroundColor: color }}
    />
    <Text className="text-[10px] text-gray-500 dark:text-gray-400">{label}</Text>
  </View>
);

type ChartTooltipEntry = {
  color?: string;
  dataKey?: string;
  name?: string;
  value?: number | string | Array<number | string>;
};

type ChartTooltipProps = {
  active?: boolean;
  payload?: ChartTooltipEntry[];
  label?: string | number;
  formatLabel: (value: string) => string;
};

const formatSingleTooltipValue = (name: string, value: number | string): string => {
  if (typeof value !== "number") return String(value);
  const normalizedName = name.toLowerCase();

  if (normalizedName.includes("rate")) return `${value.toFixed(2)}%`;
  if (normalizedName.includes("duration")) return `${value.toFixed(2)}m`;
  if (normalizedName.includes("turns")) return value.toFixed(1);
  if (normalizedName.includes("latency") || normalizedName.includes("response") || normalizedName.includes("tts") || normalizedName.includes("stt")) {
    return `${Math.round(value)}ms`;
  }
  return Math.round(value).toLocaleString();
};

const formatTooltipValue = (name: string, value: number | string | Array<number | string>): string => {
  if (Array.isArray(value)) return value.map((entry) => formatSingleTooltipValue(name, entry)).join(" – ");
  return formatSingleTooltipValue(name, value);
};

export const AnalyticsChartTooltip = ({ active, payload, label, formatLabel }: ChartTooltipProps) => {
  if (!active || !payload?.length || label === undefined) return null;

  const payloadNames = new Set(payload.map((entry) => entry.name).filter(Boolean));
  const visiblePayload = payload.filter((entry) => {
    if (entry.value === undefined) return false;
    const name = entry.name || entry.dataKey || "Value";
    if (!Array.isArray(entry.value) || !name.includes(" p50–p95")) return true;
    const seriesName = name.split(" p50–p95")[0];
    return !(payloadNames.has(`${seriesName} p50`) && payloadNames.has(`${seriesName} p95`));
  });

  return (
    <View className="min-w-[190px] rounded-md border border-gray-200 bg-white px-3 py-2 shadow-xl dark:border-gray-700 dark:bg-slate-800">
      <Text className="mb-1.5 block text-[10px] font-medium text-gray-500 dark:text-gray-400">{formatLabel(String(label))}</Text>
      <View className="space-y-1">
        {visiblePayload.map((entry, index) => {
          const name = entry.name || entry.dataKey || "Value";
          return (
            <View
              key={`${entry.dataKey || name}-${index}`}
              className="flex items-center justify-between gap-5"
            >
              <View className="flex items-center gap-1.5">
                <View
                  className="h-2 w-2 rounded-sm"
                  style={{ backgroundColor: entry.color || ANALYTICS_COLORS.primary }}
                />
                <Text className="text-[11px] text-gray-600 dark:text-gray-300">{name}</Text>
              </View>
              <Text className="text-[11px] font-semibold tabular-nums text-gray-950 dark:text-white">{formatTooltipValue(name, entry.value!)}</Text>
            </View>
          );
        })}
      </View>
    </View>
  );
};
